import React, { useCallback, useEffect, useMemo } from 'react';

import { cn } from '@crossnokaye/ux-common';

import { useBranchConfig } from '@/hooks/use-branch-config';
import { useUserState } from '@/hooks/use-user-state';
import { useNavStore } from '@/stores/nav';
import {
  DeviceTypeDetectionVariant,
  useDeviceTypeDetection,
} from '@/ux-common/hooks/use-device-type-detection/use-device-type-detection';

import { FacilitySelectionNav } from './components/facility-selection-nav';
import { MainNav } from './components/main-nav/main-nav';
import { NavOptions } from '../constants';
import { useStatefulExpandedNav } from '../hooks/use-stateful-expanded-nav';

export const LeftNavigation = () => {
  const { data: userState } = useUserState();

  const { setEnableFacilityMenu, setExpanded, expanded, activeNavMenu, setActiveNavMenu } =
    useNavStore();

  // We only want to allow the facility menu to be shown if we meet the following conditions:
  // 1. We are not in offline mode
  // 2. We have more than one facility
  const enableFacilityMenu = useMemo(
    () => (userState?.facilities?.unordered?.length ?? 0) > 1,
    [userState?.facilities?.unordered]
  );

  const { isMobile } = useDeviceTypeDetection({
    variant: DeviceTypeDetectionVariant.DeviceOnly,
  });

  useStatefulExpandedNav(expanded, setExpanded);

  useEffect(() => {
    setEnableFacilityMenu(enableFacilityMenu);
  }, [enableFacilityMenu, setEnableFacilityMenu]);

  const toggleExpanded = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded, setExpanded]);

  const switchToFacilityMenu = useCallback(() => {
    if (enableFacilityMenu) {
      setActiveNavMenu(NavOptions.Facility);
      // expand when switching to facilityMenu
      setExpanded(true);
    }
  }, [enableFacilityMenu, setActiveNavMenu, setExpanded]);

  const switchToMainNav = () => {
    setActiveNavMenu(NavOptions.Main);
  };

  const ResponsiveWrapper = isMobile ? MobileWrapper : DesktopWrapper;

  return (
    <ResponsiveWrapper expanded={expanded} data-testid={'left-nav'}>
      {activeNavMenu === NavOptions.Facility ? (
        <FacilitySelectionNav switchToMainNav={switchToMainNav} />
      ) : (
        <MainNav
          isExpanded={expanded}
          toggleExpanded={toggleExpanded}
          switchToFacilityMenu={switchToFacilityMenu}
        />
      )}
    </ResponsiveWrapper>
  );
};

const MobileWrapper = ({
  expanded,
  children,
}: {
  expanded: boolean;
  children: React.ReactNode;
}) => {
  const { showBanner } = useBranchConfig();

  return (
    <div
      className={cn(
        'fixed inset-x-0 bottom-0 z-toast bg-secondary transform transition-transform duration-300 ease-in-out',
        showBanner ? 'top-10' : 'top-0',
        {
          'translate-x-0': expanded,
          '-translate-x-full': !expanded,
        }
      )}
    >
      {children}
    </div>
  );
};

// Desktop wrapper applies the width transition
const DesktopWrapper = ({
  expanded,
  children,
}: {
  expanded: boolean;
  children: React.ReactNode;
}) => {
  return (
    <div
      className={cn(
        'bg-secondary flex-shrink-0 transition-all duration-300 ease-in-out relative z-sticky',
        {
          'w-[17.5rem]': expanded,
          'w-[4.375rem]': !expanded,
        }
      )}
    >
      {children}
    </div>
  );
};
